// Shared "create a new investor" form (16.e.1) — POST /ops/investors mints a
// lightweight account-less Client that can then be subscribed to a fund. Extracted
// from the /fund/new onboarding wizard (FundOnboard.tsx, slice 5b2b) so the SAME
// form backs both the wizard AND the ongoing-management surfaces (/investors and
// the /fund SubscribeCard) — a manager can add a client post-onboarding, not only
// during the one-time wizard. Single source of truth; no copy.
//
// onCreated returns the minimal { id, name } the callers need (select-and-subscribe
// on /fund, refetch-the-roster on /investors).
import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';

import { api } from '../lib/api';

export interface CreatedInvestor { id: string; name: string; }

function Field({ label, hint, children }: {
  label: string; hint?: string; children: ReactNode;
}) {
  return (
    <div className="field">
      <label className="field-label">{label}</label>
      {children}
      {hint && (
        <div style={{
          fontSize: 12, color: 'var(--ink-3)', marginTop: 6,
          fontFamily: 'var(--serif)', fontStyle: 'italic',
        }}>
          {hint}
        </div>
      )}
    </div>
  );
}

export function CreateInvestorForm({ onCreated, onCancel }: {
  onCreated: (inv: CreatedInvestor) => void;
  onCancel: () => void;
}) {
  const [displayName, setDisplayName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [entityType, setEntityType] = useState('llc');
  const [walletAddress, setWalletAddress] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;  // Enter mid-flight bypasses the disabled button → guard here too.
    setError(null);
    if (!displayName.trim()) { setError('Give the investor a name.'); return; }
    setBusy(true);
    try {
      const inv = await api<{ id: string; display_name: string }>('/ops/investors', {
        method: 'POST',
        body: {
          display_name: displayName.trim(),
          legal_name: legalName.trim() || displayName.trim(),
          entity_type: entityType,
          ...(walletAddress.trim() ? { wallet_address: walletAddress.trim() } : {}),
        },
      });
      onCreated({ id: inv.id, name: inv.display_name });
    } catch (err: any) {
      setError(err?.payload?.detail || 'Could not create the investor. Check the fields and try again.');
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} style={{ borderTop: '1px solid var(--ink)', paddingTop: 18,
                                     display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Field label="INVESTOR NAME" hint="Shown across the fund and on every statement.">
        <input className="field-input" required autoFocus value={displayName}
               onChange={e => setDisplayName(e.target.value)} placeholder="Acme LP" />
      </Field>
      <Field label="LEGAL NAME (OPTIONAL)" hint="Defaults to the investor name.">
        <input className="field-input" value={legalName}
               onChange={e => setLegalName(e.target.value)} placeholder="Acme Capital Partners LP" />
      </Field>
      <Field label="ENTITY TYPE">
        <select className="field-input" value={entityType} onChange={e => setEntityType(e.target.value)}>
          <option value="llc">LLC</option>
          <option value="trust">Trust</option>
          <option value="individual">Individual</option>
          <option value="other">Other</option>
        </select>
      </Field>
      <Field label="WALLET ADDRESS (OPTIONAL)" hint="On-chain address for this investor, if applicable.">
        <input className="field-input" value={walletAddress}
               onChange={e => setWalletAddress(e.target.value)} placeholder="0x…" />
      </Field>
      {error && (
        <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--oxblood)', fontSize: 14 }}>
          {error}
        </div>
      )}
      <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
        <button type="submit" className="btn" disabled={busy}>
          {busy ? 'Creating…' : <>Create + select <span className="btn-arrow">→</span></>}
        </button>
        <button type="button" className="btn-link" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
